import { useEvidenceSampleSessionDetails } from 'api';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { EvidenceSessionPhotoGallery, Grid, MediaDialog, Stack, Typography, useBreakpoint } from 'ui';

import { MaterialSampleBreakdown } from '../../../breakdowns';

export const EvidenceSampleSessionDetails = ({
  parentId,
  siteMaterialId,
}: {
  parentId: number;
  siteMaterialId: number;
}) => {
  const { t } = useTranslation('features');
  const { isMobile } = useBreakpoint();
  const { evidence, composition, material } = useEvidenceSampleSessionDetails(parentId, siteMaterialId);

  const carouselMedia = evidence.map((file) => file.fullResolutionSrc);

  const [selectedImage, setSelectedImage] = useState<string>('');

  const handleSelect = (src: string) => {
    const relatedMedia = evidence.find((file) => file.src === src || file.fullResolutionSrc === src);
    setSelectedImage(relatedMedia?.fullResolutionSrc || '');
  };

  return (
    <>
      <Grid container spacing={3} data-testid="evidence-session-details">
        <Grid
          data-testid="photo-evidence"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('evidence.sampleSessionDetails.sampleImagesTitle')}
            </Typography>
            {evidence ? (
              <EvidenceSessionPhotoGallery
                photoEvidence={evidence}
                maxImages={isMobile ? 6 : 8}
                handleClick={(photo) => handleSelect(photo.src)}
              />
            ) : (
              <Typography>{t('evidence.sampleSessionDetails.noData')}</Typography>
            )}
          </Stack>
        </Grid>
        <Grid
          data-testid="composition-report"
          size={{
            md: 6,
            xs: 12,
          }}
        >
          <Stack
            sx={{
              gap: 0.5,
            }}
          >
            <Typography variant="h6" gutterBottom fontWeight="bold">
              {t('evidence.sampleSessionDetails.manualSampleAnalysisTitle')}
            </Typography>
            {composition ? (
              <MaterialSampleBreakdown
                items={composition.breakdown.map((item) => ({
                  label: item.name_display,
                  unit: item.unit,
                  unit_display: item.unit_display,
                  value: item.value,
                }))}
                variant={material}
                precision={2}
              />
            ) : (
              <Typography>{t('evidence.sampleSessionDetails.noData')}</Typography>
            )}
          </Stack>
        </Grid>
      </Grid>
      <MediaDialog
        open={Boolean(selectedImage)}
        selectedMedium={selectedImage}
        onClose={() => setSelectedImage('')}
        media={carouselMedia}
        handleNavigate={handleSelect}
        imageCount={carouselMedia.length}
      />
    </>
  );
};
